/*----------------------------------
- UI: SPRITE ICON
----------------------------------*/

/**
 * @docs docs/features/website-seo-performance
 *
 * The website's drop-in replacement for the shared `Icon`.
 *
 * Why it exists: the shared component inlines lucide's full path data once per icon, which is what
 * put 139 `<svg>` and ~43 KB of duplicated geometry in the home document. This wrapper renders the
 * same outer `<svg>` and defers the geometry to one content-hashed sprite that the browser fetches
 * once and caches for a year.
 *
 * Why it does not replace the shared component: Product resolves icon names from stored data and is
 * not a measured bottleneck, so it keeps inlining. `SpriteIconName` is a superset of the shared
 * `IconName`, so every existing website call site type-checks unchanged and the compiler is what
 * guarantees the sprite covers the whole shared catalog.
 *
 * @rule The rendered markup must stay attribute-for-attribute what lucide-preact emitted, minus the
 * geometry: same class string, same box, same stroke. `width`/`height` carry the size instead of a
 * `viewBox` because the referenced `<symbol>` brings its own `viewBox="0 0 24 24"`, which scales the
 * shape and the inherited `stroke-width` exactly as the inline version did.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { cn } from '@uniquedomains/components/ui/utils';

import { SPRITE_ICON_IDS, SPRITE_URL, type SpriteIconName } from './spriteManifest.generated';

/*----------------------------------
- TYPES
----------------------------------*/

type IconProps = {
    name: SpriteIconName;
    class?: string;
    className?: string;
    size?: number;
    strokeWidth?: number;
    /* `color` and `fill` mirror lucide's own props: the public domains table tints and fills its row
       glyphs per row, and both attributes are inherited by the referenced symbol's shapes. */
    color?: string;
    fill?: string;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const Icon = ({
    name,
    class: classProp,
    className,
    size = 20,
    strokeWidth = 1.8,
    color = 'currentColor',
    fill = 'none',
}: IconProps) => {
    const sprite = SPRITE_ICON_IDS[name];

    if (!sprite) return null;

    return (
        <svg
            class={cn(sprite.class, classProp, className)}
            width={size}
            height={size}
            fill={fill}
            stroke={color}
            stroke-width={strokeWidth}
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
        >
            <use href={`${SPRITE_URL}#${sprite.id}`} />
        </svg>
    );
};

export default Icon;
export { Icon };
export type { SpriteIconName };
